import { CCardTitle, CCol, CFormCheck, CFormTextarea, CRow } from '@coreui/react-pro'
import React from 'react'
import { useSelector } from 'react-redux'
import Select from 'react-select'
import { BetterToggleSwitch } from 'src/components/common/Toggle/ToggleSwitch'
import { changeBillingLegalEntity } from 'src/helper/backendHelper'
import FEATURES from '../../../utils/featuresConstants'
import { getTheme } from '../../../redux/user/userSlice'
import {
  generateCareTeamByFacilityOptions,
  selectStyleDark,
  selectStyleLight,
} from '../../../utils/sharedCareTeamLogic'
import { showLongTermCare } from '../../../utils/sharedFeaturesFunctions'
import CareDxDropdown from './CareDxDropdown'

/**
 * Takes in the expected billing entity value, and formats it to match the expected Select
 * format that react-select is wanting
 */
function formatBillingEntityValue(valueObj, options) {
  if (!valueObj) {
    // if not there, nothing to do
    return null
  } else if (valueObj?.value) {
    // means the object is already formatted
    return valueObj
  } else if (valueObj?.legal_entity_name) {
    // is a complete object, so format it
    return {
      ...valueObj,
      value: valueObj.legal_entity_id,
      label: valueObj.legal_entity_name,
    }
  } else {
    // default is its just an id, so it needs a label
    const billingEntityMatch = options.find(
      (opt) => opt.legal_entity_id === valueObj.legal_entity_id,
    )
    return {
      ...billingEntityMatch,
      value: billingEntityMatch.legal_entity_id,
      label: billingEntityMatch.legal_entity_name,
    }
  }
}

/**
 * Takes in a list of billing providers, and provides a transformed options list
 */
function generateSharedServiceProviderOptions(incidentToBillingProviders) {
  if (!incidentToBillingProviders) {
    return [
      {
        label: 'None',
        value: null,
      },
    ]
  }

  let options = []
  incidentToBillingProviders?.forEach((item) => {
    options.push({
      label: `${item?.first_name} ${item?.last_name}`,
      value: item?._id,
    })
  })
  options.push({
    label: 'None',
    value: null,
  })
  return options
}

const MAX_LENGTH = 420

const AdditionalInfo = ({
  visit,
  setBillingTxt,
  billingTxt = '',
  // these represent incidentToProviderId
  selectedSharedProvider,
  setSelectedSharedProvider,
  incidentToProviderSubstantive,
  setIncidentToProviderSubstantive,
  isTeleHealth,
  setIsTeleHealth,
  isHospiceRelated,
  setIsHospiceRelated,
  isCCM,
  setIsCCM,
  isPCM,
  setIsPCM,
  inRight,
  fromEncounter,
  setBillingEntity,
  billingEntity,
  billingEntityOptions,
  careTeam,
  setCareTeam,
  isNeuroSuite,
  setIsNeuroSuite,
  isPendingCosign,
  setIsPendingCosign,
  authNumber,
  setAuthNumber,
  showCareTeam = true,
  currentFacilityId,
  isLongTermCare,
  selectedPlaceOfService,
  encounterDX,
  careManageDX,
  onCareManageDxUpdate,
}) => {
  const theme = useSelector(getTheme)
  const provider = useSelector((state) => state?.user?.provider)
  const practice = useSelector((state) => state?.user?.practice)
  const features = useSelector((state) => state?.user?.features)

  const careTeamList = practice?.careteams || []
  const careTeamsEnabled = practice?.careteamsEnabled || false
  const careTeamByFacilityOptions = generateCareTeamByFacilityOptions(
    careTeamList,
    currentFacilityId || visit?.facility_id,
    careTeam,
  )
  const selectStyles = theme === 'light' ? selectStyleLight : selectStyleDark

  const sharedServiceProviderOptions = generateSharedServiceProviderOptions(
    provider?.incident_to_billing_providers,
  )

  let selectedProviderOption = sharedServiceProviderOptions?.find(
    (item) => item?.value === selectedSharedProvider,
  )

  // if there is no selected option, check if the current provider is the substantive
  if (
    !selectedProviderOption &&
    selectedSharedProvider &&
    selectedSharedProvider === provider?._id
  ) {
    selectedProviderOption = {
      label: `${provider?.first_name} ${provider?.last_name}`,
      value: provider?._id,
    }
  }

  if (selectedProviderOption && selectedSharedProvider && incidentToProviderSubstantive) {
    selectedProviderOption.label += ' (Substantive Portion)'
  }

  return (
    <div className="aditional-info">
      <div className="section-item">
        <div className="option-holder">
          <div
            className={
              inRight ? 'info-card' : `card ProcedureCard ${fromEncounter ? 'px-4 py-4' : ''}`
            }
          >
            <div className="ProceduredHeaderContainer">
              <div className="ProcedureCardHeader">
                <CCardTitle className="ProcedureCardTitle">Additional Information</CCardTitle>
              </div>
            </div>
            <div className="Procedure-card-select-card">
              <CRow className="input-set-holder">
                <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                  <div className="lable-holder">Telehealth:</div>
                </CCol>
                <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                  <BetterToggleSwitch
                    onToggleChange={(e) => {
                      setIsTeleHealth(e?.currentTarget?.checked)
                    }}
                    isToggledOn={isTeleHealth}
                  />
                </CCol>
              </CRow>
              {visit?.patient_hospiceEnrolled && (
                <CRow className="input-set-holder">
                  <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                    <div className="lable-holder">Hospice Related:</div>
                  </CCol>
                  <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                    <BetterToggleSwitch
                      onToggleChange={(e) => {
                        setIsHospiceRelated(e?.currentTarget?.checked)
                      }}
                      isToggledOn={isHospiceRelated}
                    />
                  </CCol>
                </CRow>
              )}
              {features?.includes(FEATURES.ChronicCareMngt) && isLongTermCare && (
                <>
                  <CRow className="input-set-holder">
                    <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                      <div className="lable-holder">Chronic Care Management (CCM):</div>
                    </CCol>
                    <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                      <BetterToggleSwitch
                        onToggleChange={(e) => {
                          setIsCCM(e?.currentTarget?.checked)
                          onCareManageDxUpdate([])
                        }}
                        isToggledOn={isCCM}
                      />
                    </CCol>
                  </CRow>
                  {isCCM && (
                    <CRow className="input-set-holder">
                      <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                        <div className="lable-holder">
                          CCM treating DX{' '}
                          <span style={{ color: 'var(--input-error-message-text-color)' }}>*</span>:
                        </div>
                      </CCol>
                      <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                        <CareDxDropdown
                          limit={2}
                          encounterDX={encounterDX}
                          careDx={careManageDX}
                          setCareDx={(newDx) => {
                            onCareManageDxUpdate(newDx)
                          }}
                        />
                      </CCol>
                    </CRow>
                  )}
                </>
              )}
              {features?.includes(FEATURES.PrincipalCareMngt) &&
                showLongTermCare(features, selectedPlaceOfService) &&
                !isLongTermCare && (
                  <>
                    <CRow className="input-set-holder">
                      <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                        <div className="lable-holder">Principal Care Management (PCM):</div>
                      </CCol>
                      <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                        <BetterToggleSwitch
                          onToggleChange={(e) => {
                            setIsPCM(e?.currentTarget?.checked)
                            onCareManageDxUpdate([])
                          }}
                          isToggledOn={isPCM}
                        />
                      </CCol>
                    </CRow>
                    {isPCM && (
                      <CRow className="input-set-holder">
                        <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                          <div className="lable-holder">
                            PCM treating DX{' '}
                            <span style={{ color: 'var(--input-error-message-text-color)' }}>
                              *
                            </span>
                            :
                          </div>
                        </CCol>
                        <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                          <CareDxDropdown
                            limit={1}
                            encounterDX={encounterDX}
                            careDx={careManageDX}
                            setCareDx={(newDx) => {
                              onCareManageDxUpdate(newDx)
                            }}
                          />
                        </CCol>
                      </CRow>
                    )}
                  </>
                )}
              {features?.includes(FEATURES.NeuroSuite) && (
                <CRow className="input-set-holder">
                  <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                    <div className="lable-holder">NeuroMed:</div>
                  </CCol>
                  <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                    <BetterToggleSwitch
                      onToggleChange={(e) => {
                        setIsNeuroSuite(e?.currentTarget?.checked)
                      }}
                      isToggledOn={isNeuroSuite}
                    />
                  </CCol>
                </CRow>
              )}
              <CRow className="input-set-holder">
                <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                  <div className="lable-holder">Pending Cosign:</div>
                </CCol>
                <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                  <BetterToggleSwitch
                    onToggleChange={(e) => {
                      setIsPendingCosign(e?.currentTarget?.checked)
                    }}
                    isToggledOn={isPendingCosign}
                  />
                </CCol>
              </CRow>
              {showCareTeam && careTeamsEnabled && (
                <CRow className="input-set-holder">
                  <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                    <div className="lable-holder">Care Team:</div>
                  </CCol>
                  <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                    <Select
                      aria-label="Care Team"
                      options={careTeamByFacilityOptions}
                      styles={selectStyles}
                      onChange={(data) => {
                        setCareTeam(data.value)
                      }}
                      value={careTeamByFacilityOptions?.find((item) => item?.value === careTeam)}
                      hideSelectedOptions={true}
                    />
                  </CCol>
                </CRow>
              )}
              {sharedServiceProviderOptions.length > 1 && (
                <CRow className="input-set-holder">
                  <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                    <div className="lable-holder">Shared Service Provider:</div>
                  </CCol>
                  <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                    <Select
                      aria-label="Shared service"
                      options={sharedServiceProviderOptions}
                      styles={selectStyles}
                      onChange={(data) => {
                        setSelectedSharedProvider(data?.value)
                        // if unselecting, set substantive to false
                        setIncidentToProviderSubstantive(Boolean(data?.value))
                      }}
                      // last value is always the 'None' option
                      defaultValue={
                        sharedServiceProviderOptions[sharedServiceProviderOptions.length - 1]
                      }
                      value={selectedProviderOption}
                    />
                  </CCol>
                </CRow>
              )}

              <CRow className="input-set-holder">
                {/* Gives us the spacing we need */}
                <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}></CCol>
                <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                  {selectedSharedProvider && (
                    <CFormCheck
                      label="Substantive portion"
                      checked={incidentToProviderSubstantive}
                      key={'SubstantivePortionCheckbox'}
                      onChange={(e) => {
                        setIncidentToProviderSubstantive(e.target.checked)
                      }}
                    />
                  )}
                </CCol>
              </CRow>

              {billingEntityOptions?.length > 1 && (
                <CRow className="input-set-holder">
                  <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                    <div className="lable-holder">Billing Entity:</div>
                  </CCol>
                  <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                    <Select
                      aria-label="Billing Entity"
                      options={billingEntityOptions.map((entity) => ({
                        label: entity.legal_entity_name,
                        value: entity.legal_entity_id,
                        ...entity,
                      }))}
                      styles={selectStyles}
                      onChange={(data) => {
                        setBillingEntity(data)
                        if (visit?._id) {
                          changeBillingLegalEntity(visit._id, {
                            legal_entity_id: data.legal_entity_id,
                          })
                        }
                      }}
                      value={formatBillingEntityValue(billingEntity, billingEntityOptions)}
                    />
                  </CCol>
                </CRow>
              )}
              <CRow className="input-set-holder">
                <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>
                  <div className="lable-holder">Auth Number:</div>
                </CCol>
                <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                  <input
                    className={'authNumber'}
                    onChange={(e) => {
                      setAuthNumber(
                        e?.currentTarget?.value.slice(0, 30).replace(/[^A-Za-z0-9-]/g, ''),
                      )
                    }}
                    value={authNumber}
                    maxLength={30}
                    placeholder={'Type to add'}
                  />
                </CCol>
              </CRow>
              <CRow className="input-set-holder">
                <CCol md={inRight ? 4 : fromEncounter ? 4 : 2}>Billing Notes:</CCol>
                <CCol md={inRight ? 8 : fromEncounter ? 8 : 6}>
                  <CFormTextarea
                    type="textarea"
                    className={'default-text-area'}
                    placeholder="Type to add note"
                    rows="6"
                    value={billingTxt}
                    onChange={(e) => setBillingTxt(e?.currentTarget?.value.slice(0, MAX_LENGTH))}
                    maxLength={MAX_LENGTH}
                  />
                  <small style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {MAX_LENGTH - billingTxt.length} characters remaining
                  </small>
                </CCol>
              </CRow>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdditionalInfo
